import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Shell } from '../components/Layout.js';
import { useDocumentMeta } from '../lib/document-meta.js';
import { useI18n, useT } from '../state/i18n.js';

/** Small copy-to-clipboard button used for the starter prompts. */
function CopyButton({ text, label }: { text: string; label: string }): JSX.Element {
  const t = useT();
  const [copied, setCopied] = useState(false);
  async function copy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      setCopied(false);
    }
  }
  return (
    <button type="button" className="learn-copy" onClick={() => void copy()} aria-live="polite">
      {copied ? t({ ko: '✓ 복사됨', en: '✓ Copied' }) : label}
    </button>
  );
}

type BuilderField = {
  key: string;
  label: { ko: string; en: string };
  ph: { ko: string; en: string };
  multiline?: boolean;
};

type Builder = {
  id: string;
  label: { ko: string; en: string };
  fields: BuilderField[];
  build: (v: Record<string, string>, lang: 'ko' | 'en') => string;
};

/** Gentle placeholder when a field is empty, so the live prompt still reads. */
function g(s: string | undefined, lang: 'ko' | 'en'): string {
  const v = (s ?? '').trim();
  return v || (lang === 'ko' ? '(여기에 입력)' : '(fill this in)');
}

const BUILDERS: Builder[] = [
  {
    id: 'basic',
    label: { ko: '기본 제안서', en: 'Basic proposal' },
    fields: [
      { key: 'company', label: { ko: '회사 이름', en: 'Company name' }, ph: { ko: '예: ACME 에이전시', en: 'e.g. ACME Agency' } },
      { key: 'title', label: { ko: '제안 제목', en: 'Proposal title' }, ph: { ko: '예: BigCorp 웹 리뉴얼 제안', en: 'e.g. BigCorp website renewal' } },
      { key: 'client', label: { ko: '받는 곳 (고객사)', en: 'Client' }, ph: { ko: '예: BigCorp', en: 'e.g. BigCorp' } },
      { key: 'points', label: { ko: '핵심 내용 (한 줄에 하나씩)', en: 'Key points (one per line)' }, ph: { ko: '- 디자인 시스템 구축\n- 프론트엔드 재작성\n- 3개월', en: '- Build a design system\n- Rebuild the frontend\n- 3 months' }, multiline: true },
      { key: 'contact', label: { ko: '연락처', en: 'Contact' }, ph: { ko: '이메일 / 전화', en: 'email / phone' } },
    ],
    build: (v, lang) =>
      lang === 'ko'
        ? `당신은 제안서를 예쁜 웹페이지로 만들어 주는 디자이너입니다.
아래 내용을 "하나의 HTML 파일"로 만들어 주세요. CSS는 <style> 태그 안에 넣고, 외부 파일·라이브러리는 쓰지 마세요. 파일 하나로 완성되어야 합니다.

회사 이름: ${g(v.company, lang)}
제안 제목: ${g(v.title, lang)}
받는 곳(고객사): ${g(v.client, lang)}
핵심 내용:
${g(v.points, lang)}
연락처: ${g(v.contact, lang)}

디자인 지시: 깔끔하고 전문적으로. 여백 넉넉히. 색은 2개만(포인트 1개 + 글자용 진한 회색). 시스템 산세리프, 제목은 크고 굵게. 가운데 정렬 최대 폭 720px. 모바일에서도 잘 보이게. <meta charset="utf-8"> 포함.
완성되면 전체 HTML 코드를 보여주세요.`
        : `You are a designer who turns proposals into beautiful web pages.
Build the following as ONE HTML file. Put the CSS in a <style> tag; do not use external files or libraries. It must be complete as a single file.

Company name: ${g(v.company, lang)}
Proposal title: ${g(v.title, lang)}
Client: ${g(v.client, lang)}
Key points:
${g(v.points, lang)}
Contact: ${g(v.contact, lang)}

Design: clean and professional. Generous whitespace. Only two colors (one accent + dark gray text). System sans-serif; large bold headings. Centered body, max width 720px. Works on mobile. Include <meta charset="utf-8">.
When done, show the full HTML code.`,
  },
  {
    id: 'quote',
    label: { ko: '견적 표 포함', en: 'With a quote table' },
    fields: [
      { key: 'company', label: { ko: '회사 이름', en: 'Company name' }, ph: { ko: '예: ACME 에이전시', en: 'e.g. ACME Agency' } },
      { key: 'title', label: { ko: '제안 제목', en: 'Proposal title' }, ph: { ko: '예: BigCorp 웹 리뉴얼 견적', en: 'e.g. BigCorp renewal quote' } },
      { key: 'items', label: { ko: '항목별 견적 (항목 / 수량 / 금액)', en: 'Line items (item / qty / amount)' }, ph: { ko: '- 디자인 / 1식 / 15,000,000\n- 개발 / 1식 / 28,000,000\n- QA / 1식 / 5,000,000', en: '- Design / 1 / 15,000,000\n- Dev / 1 / 28,000,000\n- QA / 1 / 5,000,000' }, multiline: true },
      { key: 'total', label: { ko: '합계', en: 'Total' }, ph: { ko: '예: 48,000,000원 (VAT 별도)', en: 'e.g. 48,000,000 (excl. VAT)' } },
      { key: 'validUntil', label: { ko: '유효기간', en: 'Valid until' }, ph: { ko: '예: 발행일로부터 30일', en: 'e.g. 30 days from issue' } },
    ],
    build: (v, lang) =>
      lang === 'ko'
        ? `단일 HTML 파일(외부 라이브러리 없음, CSS는 <style> 안)로, "견적 표"가 포함된 제안서를 만들어 주세요.

회사 이름: ${g(v.company, lang)}
제안 제목: ${g(v.title, lang)}
항목별 견적:
${g(v.items, lang)}
합계: ${g(v.total, lang)}
유효기간: ${g(v.validUntil, lang)}

디자인 지시: 견적 표는 줄무늬 배경으로 읽기 쉽게. 합계 줄은 굵게 강조. 금액은 오른쪽 정렬 + 천 단위 콤마. 상단에 회사 이름, 하단에 유효기간 안내. 색 2개, 여백 넉넉히. <meta charset="utf-8"> 포함.
완성되면 전체 HTML 코드를 보여주세요.`
        : `As ONE HTML file (no external libraries, CSS inside <style>), build a proposal that includes a "quote table".

Company name: ${g(v.company, lang)}
Proposal title: ${g(v.title, lang)}
Line items:
${g(v.items, lang)}
Total: ${g(v.total, lang)}
Valid until: ${g(v.validUntil, lang)}

Design: striped rows for readability. Bold the total row. Right-align amounts with thousands separators. Company name at top, validity note at bottom. Two colors, generous whitespace. Include <meta charset="utf-8">.
When done, show the full HTML code.`,
  },
  {
    id: 'portfolio',
    label: { ko: '회사소개·포트폴리오', en: 'About / portfolio' },
    fields: [
      { key: 'company', label: { ko: '회사 이름', en: 'Company name' }, ph: { ko: '예: ACME 에이전시', en: 'e.g. ACME Agency' } },
      { key: 'tagline', label: { ko: '한 줄 소개', en: 'One-line intro' }, ph: { ko: '예: 브랜드를 성장시키는 웹 스튜디오', en: 'e.g. A web studio that grows brands' } },
      { key: 'strengths', label: { ko: '잘하는 것 3가지', en: '3 things we do well' }, ph: { ko: '- 브랜드 디자인\n- 웹 개발\n- 그로스', en: '- Brand design\n- Web dev\n- Growth' }, multiline: true },
      { key: 'projects', label: { ko: '대표 프로젝트 (이름 / 한 줄 설명)', en: 'Flagship projects (name / one-liner)' }, ph: { ko: '- BigCorp / 매출 2배 리뉴얼\n- StartupX / 0→1 런칭', en: '- BigCorp / 2x revenue renewal\n- StartupX / 0→1 launch' }, multiline: true },
      { key: 'why', label: { ko: '왜 우리와 일해야 하는가', en: 'Why work with us' }, ph: { ko: '- 빠른 실행\n- 데이터 기반\n- 끝까지 책임', en: '- Fast execution\n- Data-driven\n- We own the outcome' }, multiline: true },
      { key: 'contact', label: { ko: '연락처', en: 'Contact' }, ph: { ko: '이메일 / 전화', en: 'email / phone' } },
    ],
    build: (v, lang) =>
      lang === 'ko'
        ? `단일 HTML 파일(외부 라이브러리 없음, CSS는 <style> 안)로, "회사 소개 + 포트폴리오"형 제안서를 만들어 주세요.

회사 이름: ${g(v.company, lang)}
한 줄 소개: ${g(v.tagline, lang)}
우리가 잘하는 것:
${g(v.strengths, lang)}
대표 프로젝트:
${g(v.projects, lang)}
왜 우리와 일해야 하는가:
${g(v.why, lang)}
연락처: ${g(v.contact, lang)}

디자인 지시: 첫 화면에 큰 제목 + 한 줄 소개(히어로). "잘하는 것"은 카드 3개로 나란히. 프로젝트는 목록 카드로. 신뢰감 주는 차분한 색 2개. 여백 넉넉히. <meta charset="utf-8"> 포함.
완성되면 전체 HTML 코드를 보여주세요.`
        : `As ONE HTML file (no external libraries, CSS inside <style>), build an "about us + portfolio" style proposal.

Company name: ${g(v.company, lang)}
One-line intro: ${g(v.tagline, lang)}
What we do well:
${g(v.strengths, lang)}
Flagship projects:
${g(v.projects, lang)}
Why work with us:
${g(v.why, lang)}
Contact: ${g(v.contact, lang)}

Design: big title + one-line intro on the first screen (hero). "What we do well" as three side-by-side cards. Projects as list cards. Two calm, trustworthy colors. Generous whitespace. Include <meta charset="utf-8">.
When done, show the full HTML code.`,
  },
];

function PromptBuilder({ lang }: { lang: 'ko' | 'en' }): JSX.Element {
  const t = useT();
  const [typeId, setTypeId] = useState<string>(BUILDERS[0]!.id);
  const [values, setValues] = useState<Record<string, string>>({});
  const active = BUILDERS.find((b) => b.id === typeId) ?? BUILDERS[0]!;
  const prompt = active.build(values, lang);
  const set = (key: string, val: string): void => setValues((s) => ({ ...s, [key]: val }));

  return (
    <div className="learn-builder">
      <div className="learn-seg" role="group" aria-label={t({ ko: '제안서 종류', en: 'Proposal type' })}>
        {BUILDERS.map((b) => (
          <button
            key={b.id}
            type="button"
            className={b.id === typeId ? 'is-active' : ''}
            aria-pressed={b.id === typeId}
            onClick={() => setTypeId(b.id)}
          >
            {t(b.label)}
          </button>
        ))}
      </div>

      <div className="learn-builder__grid">
        <form className="learn-builder__form" onSubmit={(e) => e.preventDefault()}>
          {active.fields.map((f) => (
            <label key={f.key} className={f.multiline ? 'learn-field learn-field--wide' : 'learn-field'}>
              <span>{t(f.label)}</span>
              {f.multiline ? (
                <textarea
                  rows={4}
                  value={values[f.key] ?? ''}
                  placeholder={t(f.ph)}
                  onChange={(e) => set(f.key, e.target.value)}
                />
              ) : (
                <input
                  value={values[f.key] ?? ''}
                  placeholder={t(f.ph)}
                  onChange={(e) => set(f.key, e.target.value)}
                />
              )}
            </label>
          ))}
        </form>

        <div className="learn-builder__out">
          <div className="learn-prompt__head">
            <div>
              <h4>{t({ ko: '완성된 프롬프트', en: 'Your ready prompt' })}</h4>
              <p className="learn-prompt__hint">
                {t({ ko: '복사해서 AI 채팅에 붙여넣으세요', en: 'Copy and paste into an AI chat' })}
              </p>
            </div>
            <CopyButton text={prompt} label={t({ ko: '복사', en: 'Copy' })} />
          </div>
          <pre className="learn-prompt__body">{prompt}</pre>
        </div>
      </div>
    </div>
  );
}

/** ★언어: 이 화면의 모든 유저 노출 문자열은 t({ko,en})로만 낸다(하드코딩 금지) — 각 항목은 { ko, en } 쌍으로 두고 렌더 시점에 t()로 고른다. */
const CONTENT = {
  metaTitle: { ko: '배우기 — 코딩 없이 예쁜 HTML 제안서 만들기', en: 'Learn — Make a beautiful HTML proposal, no coding' },
  kicker: { ko: '배우기 · 초보 가이드', en: 'Learn · Beginner guide' },
  h1: {
    ko: (
      <>
        코딩 0에서 <span className="learn-hl">예쁜 HTML 제안서</span>까지
      </>
    ),
    en: (
      <>
        From zero coding to a <span className="learn-hl">beautiful HTML proposal</span>
      </>
    ),
  },
  lede: {
    ko: 'HTML이 뭔지 몰라도 괜찮아요. AI에게 말로 시켜서 제안서를 웹페이지로 만드는 법을 5분 안에 배웁니다. 완성한 파일은 그대로 CloudBTL에 올려 추적 링크로 보내면 됩니다.',
    en: "You don't need to know what HTML is. In 5 minutes you'll learn to make a proposal web page just by describing it to an AI. Upload the finished file to CloudBTL and send it as a tracked link.",
  },
  ctaStart: { ko: '3단계로 시작하기', en: 'Start in 3 steps' },
  ctaUpload: { ko: '바로 업로드하러 가기', en: 'Go upload now' },
  whyTitle: { ko: '왜 HTML 제안서일까?', en: 'Why an HTML proposal?' },
  cards: [
    {
      ico: '🔗',
      h: { ko: '링크 하나로 전달', en: 'Send one link' },
      p: { ko: '파일 첨부 대신 링크. 상대는 클릭 한 번으로 열어봐요.', en: 'A link instead of an attachment. They open it in one click.' },
    },
    {
      ico: '👀',
      h: { ko: '누가 읽었는지 보여요', en: 'See who read it' },
      p: { ko: '열었는지, 어디서 오래 머물렀는지 CloudBTL이 알려줍니다.', en: 'CloudBTL tells you if they opened it and where they lingered.' },
    },
    {
      ico: '✨',
      h: { ko: '내 브랜드로 예쁘게', en: 'Your brand, polished' },
      p: { ko: 'PDF보다 살아있고, 로고·색을 입혀 전문적으로 보여요.', en: 'More alive than a PDF — add your logo and colors to look pro.' },
    },
  ],
  conceptTitle: { ko: '‘바이브 코딩’이 뭐예요?', en: 'What is "vibe coding"?' },
  concept: {
    ko: (
      <>
        코드를 직접 짜지 않고, <strong>AI에게 원하는 걸 말로 설명해서</strong> 웹페이지를 만드는
        방식이에요. “이런 제안서를 예쁘게 만들어줘”라고 부탁하면 AI가 HTML 코드를 써줍니다. 우리는 그
        코드를 파일로 저장만 하면 끝. 문법을 외울 필요가 없어요.
      </>
    ),
    en: (
      <>
        Instead of writing code yourself, you <strong>describe what you want to an AI</strong> and it
        builds the web page. Ask “make me a nice proposal like this” and the AI writes the HTML. You
        just save the code to a file. No syntax to memorize.
      </>
    ),
  },
  stepsTitle: { ko: '딱 3단계', en: 'Just 3 steps' },
  steps: [
    {
      h: { ko: 'AI 채팅을 연다', en: 'Open an AI chat' },
      p: {
        ko: (
          <>
            아무 AI나 괜찮아요 — Claude, ChatGPT, Gemini 중 하나. 처음이라면{' '}
            <strong>Claude(claude.ai)</strong>를 추천해요. 결과 HTML을 화면 옆에서 바로
            미리보기(Artifacts)로 볼 수 있어 편합니다.
          </>
        ),
        en: (
          <>
            Any AI works — Claude, ChatGPT, or Gemini. If it's your first time, we recommend{' '}
            <strong>Claude (claude.ai)</strong>: it previews the HTML right beside the chat
            (Artifacts).
          </>
        ),
      },
    },
    {
      h: { ko: '아래 프롬프트를 복붙하고 빈칸을 채운다', en: 'Paste a prompt and fill the blanks' },
      p: {
        ko: (
          <>
            프롬프트 = AI에게 주는 지시문이에요. 대괄호 <code>[ ]</code> 부분만 내 내용으로 바꾸고
            채팅에 붙여넣으면 됩니다.
          </>
        ),
        en: (
          <>
            A prompt is the instruction you give the AI. Just replace the bracket <code>[ ]</code>{' '}
            parts with your content and paste it into the chat.
          </>
        ),
      },
    },
    {
      h: { ko: '저장하고 CloudBTL에 올린다', en: 'Save it and upload to CloudBTL' },
      p: {
        ko: (
          <>
            AI가 준 코드를 메모장(또는 텍스트 편집기)에 붙여넣고 <code>제안서.html</code> 이름으로
            저장하세요. 그 파일을 <Link to="/app">업로드 화면</Link>에 올리면 추적 링크가
            만들어집니다.
          </>
        ),
        en: (
          <>
            Paste the AI's code into a text editor and save it as <code>proposal.html</code>. Then drop
            that file on the <Link to="/app">upload page</Link> to get a tracked link.
          </>
        ),
      },
    },
  ],
  promptsTitle: { ko: '제안서 프롬프트 만들기', en: 'Build your proposal prompt' },
  promptsLede: {
    ko: (
      <>
        종류를 고르고 <strong>빈칸을 채우면</strong>, 바로 복사할 수 있는 프롬프트가 오른쪽에서
        완성됩니다. 대괄호 찾을 필요 없어요.
      </>
    ),
    en: (
      <>
        Pick a type and <strong>fill the blanks</strong> — a ready-to-copy prompt is assembled on the
        right. No brackets to hunt for.
      </>
    ),
  },
  copyPrompt: { ko: '프롬프트 복사', en: 'Copy prompt' },
  copy: { ko: '복사', en: 'Copy' },
  prompts: [
    {
      title: { ko: '① 기본 제안서', en: '① Basic proposal' },
      hint: { ko: '가장 무난한 한 장짜리 제안서', en: 'The safe, one-page proposal' },
      body: {
        ko: `당신은 제안서를 웹페이지로 만들어 주는 디자이너입니다.
아래 내용을 "하나의 HTML 파일"로 만들어 주세요. CSS는 <style> 태그 안에 넣고,
외부 파일이나 라이브러리는 쓰지 마세요. 파일 하나만으로 완성되어야 합니다.

[회사 이름]:
[제안 제목]:
[받는 곳(고객사)]:
[핵심 내용 3~5줄]:
[연락처]:

디자인 지시:
- 깔끔하고 전문적인 느낌. 여백을 넉넉히.
- 색은 2개만 (포인트 색 1개 + 글자용 진한 회색).
- 폰트는 시스템 기본 산세리프. 제목은 크고 굵게.
- 가운데 정렬된 최대 폭 720px 본문.
- 모바일에서도 잘 보이게.
완성되면 전체 HTML 코드를 보여주세요.`,
        en: `You are a designer who turns proposals into web pages.
Build the following as a SINGLE HTML file. Put the CSS inside a <style> tag,
and do NOT use any external files or libraries. It must be complete as one file.

[Company name]:
[Proposal title]:
[Client]:
[Key points, 3-5 lines]:
[Contact]:

Design instructions:
- Clean and professional. Generous whitespace.
- Only two colors (one accent + a dark gray for text).
- System sans-serif font. Large, bold headings.
- Centered body, max width 720px.
- Looks good on mobile too.
When done, show the full HTML code.`,
      },
    },
    {
      title: { ko: '② 견적 표 포함', en: '② With a quote table' },
      hint: { ko: '항목·금액이 있는 제안서', en: 'A proposal with line items and prices' },
      body: {
        ko: `위와 같은 규칙(단일 HTML 파일, 외부 라이브러리 없음)으로,
"견적 표"가 포함된 제안서를 만들어 주세요.

[회사 이름]:
[제안 제목]:
[항목별 견적] (항목 / 수량 / 금액):
-
-
-
[합계]:
[유효기간]:

디자인 지시:
- 견적 표는 줄무늬 배경으로 읽기 쉽게.
- 합계 줄은 굵게 강조.
- 금액은 오른쪽 정렬, 천 단위 콤마.
- 상단에 회사 이름, 하단에 유효기간 안내.`,
        en: `Using the same rules (single HTML file, no external libraries),
build a proposal that includes a "quote table".

[Company name]:
[Proposal title]:
[Line items] (item / qty / amount):
-
-
-
[Total]:
[Valid until]:

Design instructions:
- Striped rows so the table is easy to read.
- Bold the total row.
- Right-align amounts with thousands separators.
- Company name at top, validity note at the bottom.`,
      },
    },
    {
      title: { ko: '③ 회사소개 · 포트폴리오형', en: '③ About-us / portfolio' },
      hint: { ko: '우리를 소개하고 신뢰를 주는 형태', en: 'Introduce your team and build trust' },
      body: {
        ko: `위와 같은 규칙(단일 HTML 파일, 외부 라이브러리 없음)으로,
"회사 소개 + 포트폴리오"형 제안서를 만들어 주세요.

[회사 이름]:
[한 줄 소개]:
[우리가 잘하는 것 3가지]:
[대표 프로젝트 2~3개] (이름 / 한 줄 설명):
-
-
[왜 우리와 일해야 하는가 3줄]:
[연락처]:

디자인 지시:
- 첫 화면에 큰 제목 + 한 줄 소개 (히어로).
- "잘하는 것"은 카드 3개로 나란히.
- 프로젝트는 목록 카드로.
- 신뢰감 주는 차분한 색.`,
        en: `Using the same rules (single HTML file, no external libraries),
build an "about us + portfolio" style proposal.

[Company name]:
[One-line intro]:
[3 things we do well]:
[2-3 flagship projects] (name / one-line description):
-
-
[Why work with us, 3 lines]:
[Contact]:

Design instructions:
- Big title + one-line intro on the first screen (hero).
- "What we do well" as three side-by-side cards.
- Projects as list cards.
- Calm, trustworthy colors.`,
      },
    },
  ],
  tipsTitle: { ko: '더 예쁘게 만드는 법', en: 'Make it prettier' },
  tipsLede: { ko: '결과가 밋밋하면, 아래 문장을 이어서 보내 “다시 다듬어줘”라고 하면 됩니다.', en: 'If the result looks plain, send the lines below and ask it to “refine this”.' },
  designTitle: { ko: '디자인 보정 요청', en: 'Design tweak request' },
  designHint: { ko: '한 줄씩 골라 붙여도 돼요', en: 'Pick a line or two' },
  designSnippet: {
    ko: `추가 디자인 요청 (복붙해서 이어 붙이세요):
- 포인트 색을 #0EA5A4(청록)으로 해줘.
- 섹션 사이 간격을 더 넉넉하게.
- 제목 폰트를 더 크고 굵게.
- 표/카드에 은은한 그림자와 둥근 모서리.
- 인쇄해도 깔끔하게 (배경색 최소).`,
    en: `Extra design requests (copy and paste to follow up):
- Make the accent color #0EA5A4 (teal).
- Add more space between sections.
- Make heading fonts bigger and bolder.
- Soft shadows and rounded corners on tables/cards.
- Print cleanly (minimal background color).`,
  },
  tips: [
    {
      ko: (
        <>
          <strong>수정은 말로:</strong> “2번 섹션 색을 남색으로”, “표를 더 크게”처럼 그냥 얘기하면
          AI가 고쳐줍니다.
        </>
      ),
      en: (
        <>
          <strong>Edit by talking:</strong> just say “make section 2 navy”, “make the table bigger” and
          the AI fixes it.
        </>
      ),
    },
    {
      ko: (
        <>
          <strong>색은 2개만:</strong> 포인트 색 하나 + 진한 회색 글자. 너무 많으면 지저분해 보여요.
        </>
      ),
      en: (
        <>
          <strong>Only two colors:</strong> one accent + a dark gray for text. Too many looks messy.
        </>
      ),
    },
    {
      ko: (
        <>
          <strong>여백이 고급스러움을 만든다:</strong> “여백을 더 넉넉히”는 마법의 주문.
        </>
      ),
      en: (
        <>
          <strong>Whitespace = premium:</strong> “add more whitespace” is the magic phrase.
        </>
      ),
    },
    {
      ko: (
        <>
          <strong>이미지는 URL로:</strong> 온라인에 있는 이미지 주소를 주며 “여기에 이 로고 넣어줘”.
        </>
      ),
      en: (
        <>
          <strong>Images by URL:</strong> give an online image address and say “put this logo here”.
        </>
      ),
    },
  ],
  faqTitle: { ko: '자주 막히는 것', en: 'Common snags' },
  faq: [
    {
      q: { ko: '파일이 여러 개로 나왔어요', en: 'It gave me multiple files' },
      a: {
        ko: (
          <>
            “CSS를 HTML 안 &lt;style&gt; 태그에 넣어서 <strong>파일 하나로</strong> 다시 만들어줘”라고
            요청하세요. CloudBTL은 단일 HTML 파일을 올립니다.
          </>
        ),
        en: (
          <>
            Ask: “put the CSS inside a &lt;style&gt; tag so it's <strong>one single file</strong>”.
            CloudBTL uploads a single HTML file.
          </>
        ),
      },
    },
    {
      q: { ko: '한글이 깨져요', en: 'The text looks garbled' },
      a: {
        ko: (
          <>
            “문서 맨 위에 <code>&lt;meta charset="utf-8"&gt;</code>를 넣어줘”라고 하거나, 저장할 때
            인코딩을 UTF-8로 선택하세요.
          </>
        ),
        en: (
          <>
            Ask it to “add <code>&lt;meta charset="utf-8"&gt;</code> at the top”, or choose UTF-8
            encoding when you save.
          </>
        ),
      },
    },
    {
      q: { ko: '어떻게 저장하죠?', en: 'How do I save it?' },
      a: {
        ko: (
          <>
            코드를 복사 → 메모장(Windows)이나 텍스트 편집(Mac)에 붙여넣기 → “다른 이름으로 저장” →
            파일명을 <code>제안서.html</code>로 (확장자 .html 꼭). Mac이면 “일반 텍스트” 형식으로
            저장하세요.
          </>
        ),
        en: (
          <>
            Copy the code → paste into Notepad (Windows) or TextEdit (Mac) → “Save As” → name it{' '}
            <code>proposal.html</code> (the .html extension matters). On Mac, save as “Plain Text”.
          </>
        ),
      },
    },
    {
      q: { ko: '몇 페이지짜리로 만들고 싶어요', en: 'I want multiple pages' },
      a: {
        ko: (
          <>
            HTML은 길게 스크롤되는 한 장이에요. “섹션을 나눠서 길게 만들어줘”라고 하면 됩니다. 인쇄 시
            페이지는 자동으로 나뉩니다.
          </>
        ),
        en: (
          <>
            HTML is one long scrolling page. Just say “split it into sections and make it longer”. It
            splits into pages automatically when printed.
          </>
        ),
      },
    },
  ],
  finalTitle: { ko: '준비됐나요?', en: 'Ready?' },
  finalP: { ko: '프롬프트 하나 복사해서 AI에 붙여넣는 걸로 시작해보세요. 5분이면 충분합니다.', en: 'Start by copying one prompt and pasting it into an AI. Five minutes is plenty.' },
  finalCta: { ko: '만든 HTML 업로드하러 가기', en: 'Go upload your HTML' },
};

export function LearnPage(): JSX.Element {
  const { lang } = useI18n();
  const t = useT();
  useDocumentMeta({ title: t(CONTENT.metaTitle), noindex: true });

  return (
    <Shell>
      <div className="learn">
        <header className="learn-hero">
          <span className="learn-kicker">{t(CONTENT.kicker)}</span>
          <h1>{t(CONTENT.h1)}</h1>
          <p className="learn-lede">{t(CONTENT.lede)}</p>
          <div className="learn-hero__cta">
            <a href="#steps" className="cta-primary">
              {t(CONTENT.ctaStart)}
            </a>
            <Link to="/app" className="cta-secondary">
              {t(CONTENT.ctaUpload)}
            </Link>
          </div>
        </header>

        <section className="learn-why">
          <h2>{t(CONTENT.whyTitle)}</h2>
          <div className="learn-cards">
            {CONTENT.cards.map((c) => (
              <div className="learn-card" key={c.h.ko}>
                <div className="learn-card__ico">{c.ico}</div>
                <h3>{t(c.h)}</h3>
                <p>{t(c.p)}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="learn-concept">
          <h2>{t(CONTENT.conceptTitle)}</h2>
          <p>{t(CONTENT.concept)}</p>
        </section>

        <section className="learn-steps" id="steps">
          <h2>{t(CONTENT.stepsTitle)}</h2>
          {CONTENT.steps.map((s, i) => (
            <div className="learn-step" key={s.h.ko}>
              <div className="learn-step__num">{i + 1}</div>
              <div className="learn-step__body">
                <h3>{t(s.h)}</h3>
                <p>{t(s.p)}</p>
              </div>
            </div>
          ))}
        </section>

        <section className="learn-prompts">
          <h2>{t(CONTENT.promptsTitle)}</h2>
          <p className="learn-section-lede">{t(CONTENT.promptsLede)}</p>
          <PromptBuilder lang={lang} />
        </section>

        <section className="learn-tips">
          <h2>{t(CONTENT.tipsTitle)}</h2>
          <p className="learn-section-lede">{t(CONTENT.tipsLede)}</p>
          <div className="learn-prompt">
            <div className="learn-prompt__head">
              <div>
                <h4>{t(CONTENT.designTitle)}</h4>
                <p className="learn-prompt__hint">{t(CONTENT.designHint)}</p>
              </div>
              <CopyButton text={t(CONTENT.designSnippet)} label={t(CONTENT.copy)} />
            </div>
            <pre className="learn-prompt__body">{t(CONTENT.designSnippet)}</pre>
          </div>
          <ul className="learn-tiplist">
            {CONTENT.tips.map((tip, i) => (
              <li key={i}>{t(tip)}</li>
            ))}
          </ul>
        </section>

        <section className="learn-faq">
          <h2>{t(CONTENT.faqTitle)}</h2>
          {CONTENT.faq.map((f) => (
            <details key={f.q.ko}>
              <summary>{t(f.q)}</summary>
              <p>{t(f.a)}</p>
            </details>
          ))}
        </section>

        <section className="learn-final">
          <h2>{t(CONTENT.finalTitle)}</h2>
          <p>{t(CONTENT.finalP)}</p>
          <Link to="/app" className="cta-primary">
            {t(CONTENT.finalCta)}
          </Link>
        </section>
      </div>
    </Shell>
  );
}
